import { normalizeAreaIdForLevel } from "../idUtils.js";
import { getFeatureCentroid, getConcentrationSet, findFeatureForAreaId, getFeatureDisplayName } from "./geoUtils.js";
import { makeHeatScale, make2pointHeatScale } from "../colorScale.js";
import { isTrackingSchema } from "../schemaTypes.js";

/**
 * Pure computational core of MapPage.restyle().
 *
 * Verbatim lift: this.state.X → inputs.X, this.props.X → inputs.X,
 * extracted helpers called as direct imports.  Returns all output channels
 * as data; the wrapper dispatches them.
 *
 * ⚠  Row mutation: inputs.metricRows objects are mutated in place
 *    (row.Net, row.net1, row.ActionTargetKey).  Preserved verbatim.
 *
 * ⚠  Channel 5 NaN: filter mode + TargetSubVariable1="net" + array polarity
 *    → d[arrayKey] = undefined → min/max = NaN.  Preserved verbatim.
 *
 * Return shape:
 *   styledGeo        GeoJSON FC | null   Ch1 (null = !geoData early exit)
 *   ranked           Array | null        Ch2 (null = !geoData early exit)
 *   compareArrows    GeoJSON FC | null   Ch3
 *   rankedToNotify   Array | null        Ch4 (null = !geoData early exit)
 *   maxRangesToSet   object | null       Ch5 (null = no-op)
 *   waveOptionsToSet Array | null        Ch6 (null = no-op; wrap in setTimeout)
 *   waveSelectToSet  number | null       Ch7 (null = no-op; wrap in setTimeout)
 */
export function computeRestyle(inputs) {
  // Channel accumulators — populated at dispatch points; returned for wrapper.
  let waveOptionsToSet = null;
  let waveSelectToSet  = null;
  let maxRangesToSet   = null;

    const { geoData, metricRows } = inputs;

  
    if (!geoData) {
      return { styledGeo: null, ranked: null, compareArrows: null,
               rankedToNotify: null, maxRangesToSet: null,
               waveOptionsToSet: null, waveSelectToSet: null };
    }
    const group = inputs.group;
    if (!group || (!group.keys?.length && inputs.ActionMode==="explore")  || !Array.isArray(metricRows) || (!group?.TargetVariable1?.keys?.length && inputs.ActionMode==="filter")) {
      const neutral = {
        type: "FeatureCollection",
        features: geoData.features.map(f => ({
          ...f,
          properties: { ...f.properties, fillColor: "#ffffff", topLabel: "", topValue: 0 }
        }))
      };
      return { styledGeo: neutral, ranked: [], compareArrows: null,
               rankedToNotify: [],
               maxRangesToSet: null,
               waveOptionsToSet, waveSelectToSet };
    }

    let filteredMetricRows = metricRows;
    const activeSchema =
      inputs.ActionMode === "filter"
        ? group?.TargetVariable1?.schema
        : group?.schema;
  
    if (isTrackingSchema(activeSchema)) {
      // Extract unique waves and sort numerically
      console.log("METRICROWS: metricRows",metricRows)
      const metricKeys = group?.keys || [];

        // In "filter" (Find Target Areas) mode getActiveGroup returns a WRAPPER,
        // { TargetVariable1: g1 }, which has no `waves` of its own — the waves
        // live on TargetVariable1. Reading group.waves therefore came back
        // undefined and fell through to deriving the wave list from whatever rows
        // the fetch returned. Since fetchMetrics deliberately sends no wave for
        // tracking schemas, that is every wave in the table, so a wave-4-only
        // question like self_drive_change offered waves 1-4 in the selector and
        // three of them mapped to nothing. Resolve against the same variable the
        // schema check above uses.
        const activeWavesGroup = inputs.ActionMode === "filter"
          ? group?.TargetVariable1
          : group;
        const uniqueWaves = Array.isArray(activeWavesGroup?.waves)
          ? [...activeWavesGroup.waves].sort((a, b) => Number(b) - Number(a))
          : [...new Set((metricRows || []).map(r => r?.wave).filter(w => w != null))]
              .sort((a, b) => Number(b) - Number(a));
        console.log("uniqueWaves")

      // Compare to the CURRENT prop value so we re-dispatch whenever the
      // parent resets waveOptions (e.g. on group change). Use defer (setTimeout)
      // to escape the synchronous componentDidUpdate cycle — this is what
      // prevents the Redux dispatch → re-render → componentDidUpdate loop.
      const currentWaveOptions = Array.isArray(inputs.waveOptions) ? inputs.waveOptions : [];
      const needsWaveOptionsUpdate =
        (currentWaveOptions.length === 0 && uniqueWaves.length > 0) ||
        (uniqueWaves.length > 1 && JSON.stringify(uniqueWaves) !== JSON.stringify(currentWaveOptions));

      if (needsWaveOptionsUpdate) {
        const captured = uniqueWaves.slice();
        waveOptionsToSet = captured.slice(); // Ch6: wrapper dispatches via setTimeout
      }

      // Set default wave selection — only when not yet set, deferred to avoid loop
      if (inputs.waveSelect == null && uniqueWaves.length > 0) {
        const first = uniqueWaves[0];
        waveSelectToSet = first; // Ch7: wrapper dispatches via setTimeout
      }
      
      // Compare latest two waves or filter to selected wave
      if (inputs.compareWaves) {
        const deltaRows = Array.isArray(inputs.compareMetricRows) ? inputs.compareMetricRows : [];
        if (deltaRows.length) {
          filteredMetricRows = deltaRows;
          const keys = inputs.ActionMode === "filter"
            ? (group?.TargetVariable1?.keys || [])
            : (group?.keys || []);
          const sample = deltaRows[0] || {};
          const sampleKey = keys[0];
          const vals = deltaRows
            .map((r) => Number(r?.[sampleKey]))
            .filter((v) => Number.isFinite(v));
          const min = vals.length ? Math.min(...vals) : null;
          const max = vals.length ? Math.max(...vals) : null;
          console.log("[compare] delta rows", {
            level: inputs.geoLevel,
            wavePair: inputs.compareWavePair,
            rows: deltaRows.length,
            sampleKey,
            min,
            max,
            sample,
          });
        }
      } else if (inputs.waveSelect != null) {
        filteredMetricRows = metricRows.filter(r => Number(r.wave) === Number(inputs.waveSelect));
      }
    }
      console.log("filteredMetricRows",filteredMetricRows)
// --- Helpers for Arrays & Colors ---
    const sumKeys = (keyOrKeys, row) => {
      if (Array.isArray(keyOrKeys)) return keyOrKeys.reduce((acc, k) => acc + (Number(row[k]) || 0), 0);
      return Number(row[keyOrKeys]) || 0;
    };

    const getFirstKey = (keyOrKeys) => Array.isArray(keyOrKeys) ? keyOrKeys[0] : keyOrKeys;

    // Single source of truth for "what number does this heatKey mean for this row".
    // Composite keys ("net", "negsum", "sum:a|b") are not columns, so a bare
    // row[heatKey] lookup yields undefined → NaN → every district paints with the
    // no-data grey. Both the scale domain and the per-feature fill must read
    // through here or they silently disagree.
    const readMetric = (row, key) => {
      if (!row || !key) return NaN;
      if (key === "net") {
        const p = sumKeys(group?.polarity?.pos, row);
        const n = sumKeys(group?.polarity?.neg, row);
        return (Number.isFinite(p) && Number.isFinite(n)) ? p - n : NaN;
      }
      if (key === "negsum") return sumKeys(group?.polarity?.neg, row);
      if (typeof key === "string" && key.startsWith("sum:")) {
        return sumKeys(key.slice(4).split("|"), row);
      }
      const v = Number(row[key]);
      return Number.isFinite(v) ? v : NaN;
    };

    // Smart Color Lookup: Checks Dictionary -> Global Map -> Polarity Defaults
    const getColor = (key) => {
      const dictColors = group?.colors || {};
      if (dictColors[key]) return dictColors[key];
      if (colors && colors[key]) return colors[key];
      
      if (group?.polarity) {
        const { pos, neg, neu } = group.polarity;
        const isIn = (bucket, k) => Array.isArray(bucket) ? bucket.includes(k) : bucket === k;
        if (isIn(pos, key)) return "#2E7D32"; // Green
        if (isIn(neg, key)) return "#C62828"; // Red
        if (isIn(neu, key)) return "#9E9E9E"; // Grey
      }
      return "#6e6e6e";
    };
    const mixWithWhite = (hex, t) => {
      const raw = String(hex || "").replace("#", "");
      if (raw.length !== 6) return "#dbeafe";
      const n = parseInt(raw, 16);
      if (!Number.isFinite(n)) return "#dbeafe";
      const rgb = { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
      const k = Math.max(0, Math.min(1, t));
      const mix = (c) => Math.round(255 - (255 - c) * k);
      return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`;
    };
    if(inputs.ActionMode==="filter"){

      
        let minTargetvar1
        let maxTargetvar1
        if(inputs.ActionTargetSubVariables.TargetSubVariable1==="net"){

                  minTargetvar1 = Math.min(
                    ...filteredMetricRows.map(d => d[group.TargetVariable1.polarity.pos]-d[group.TargetVariable1.polarity.neg])
                  );
              maxTargetvar1 = Math.max(
                    ...filteredMetricRows.map(d => d[group.TargetVariable1.polarity.pos]-d[group.TargetVariable1.polarity.neg])
                  );

            }else{
              minTargetvar1 = Math.min(
                    ...filteredMetricRows.map(d => d[inputs.ActionTargetSubVariables.TargetSubVariable1])
                  );
              maxTargetvar1 = Math.max(
                    ...filteredMetricRows.map(d => d[inputs.ActionTargetSubVariables.TargetSubVariable1])
                  );
            }
          maxRangesToSet = { val: [minTargetvar1 ?? 0, maxTargetvar1 ?? 1], varId: "1" }; // Ch5: wrapper dispatches

          }
    const byId = new Map();
    const normalizeAreaId = (id) => normalizeAreaIdForLevel(inputs.geoLevel, id);
    // Two source rows can normalize to the same key (DC ships as both 1100 and
    // 1198, one of them all-NULL). Never let a data-less row overwrite a
    // data-bearing one — otherwise DC flickers blank by row order.
    const rowHasData = (r) => Object.keys(r).some((k) => k !== "area_id" && k !== "wave" && typeof r[k] === "number" && Number.isFinite(r[k]) && k.includes("_pred"));
    for (const r of filteredMetricRows) {
      const id = normalizeAreaId(r.area_id ?? "");
      if (!id) continue;
      const existing = byId.get(id);
      if (existing && rowHasData(existing) && !rowHasData(r)) continue;
      byId.set(id, r);
    }

    let heatScale
    let heatKey
    let mode
    const { colorLabels: labels, colorMap: colors } = inputs;

    if (inputs.ActionMode === "explore") {
      mode = inputs.mode === "heat" ? "heat" : "top";
    
      const groupIsLikert = group?.type === "likert" && group?.polarity?.pos && group?.polarity?.neg;
    
      // --- define which key to use ---
      if (groupIsLikert && (!inputs.preferenceFilter || inputs.preferenceFilter === "net")) {
        heatKey = "net";
      } else if (typeof inputs.preferenceFilter === "string" && inputs.preferenceFilter.startsWith("sum:")) {
        // Composite metric: "sum:keyA|keyB|keyC" ramps on the summed share of an
        // arbitrary subset of a group's keys. Lets a panel map exactly the bloc its
        // chart shows (e.g. the human-centered cluster) instead of forcing a
        // winner-take-all map, which goes solid whenever one option dominates.
        heatKey = inputs.preferenceFilter;
      } else if (groupIsLikert && inputs.preferenceFilter === "negsum") {
        // Combined-negative: the sum of the polarity.neg keys, on its own rather
        // than netted against pos. Scroll stories quote "combined negative" as a
        // share (e.g. 53% nationally); `net` is a different quantity (corr −0.71
        // against it here), so colouring by net would contradict the panel text.
        heatKey = "negsum";
      } else if (inputs.preferenceFilter && group.keys.includes(inputs.preferenceFilter)) {
        heatKey = inputs.preferenceFilter;
      } else {
        heatKey = group.keys[0];
      }
    
      // --- compute values array ---
      const values = geoData.features.map(f => {
        const rid = normalizeAreaId(f.properties?.area_id ?? "");
        const row = byId.get(rid);
        if (!row) return NaN;
    
        const v = readMetric(row, heatKey);
        if (heatKey === "net") row.Net = v;
        else if (heatKey === "negsum") row.NegSum = v;
        else if (typeof heatKey === "string" && heatKey.startsWith("sum:")) row.CompositeSum = v;
        return v;
      });

      // --- build appropriate color scale ---
      // `heatDomain` ([min, max]) pins the ramp to an absolute scale so colour
      // encodes strength of response rather than rank within the metric. Omitted
      // → falls back to the data's own min–max (relative contrast).
      if (typeof heatKey === "string" && heatKey.startsWith("sum:")) {
        // Ramp in the colour of the bloc's lead key, so map and chart agree.
        heatScale = makeHeatScale(getColor(heatKey.slice(4).split("|")[0]), values, inputs.heatDomain);
      } else if (heatKey === "negsum") {
        // Single-hue ramp over combined negative (never 0–100), so the tech-hub
        // spread stays legible. `heatColor` lets scroll stories key the ramp to
        // the active lens (e.g. blue for Harris voters, red for Trump voters).
        heatScale = makeHeatScale(inputs.heatColor || getColor(getFirstKey(group.polarity.neg)), values, inputs.heatDomain);
      } else if (heatKey === "net") {
        const posColor = getColor(getFirstKey(group.polarity.pos));
        const negColor = getColor(getFirstKey(group.polarity.neg));
        heatScale = make2pointHeatScale(posColor, negColor, values);
      } else if (inputs.compareWaves) {
        heatScale = make2pointHeatScale("#2E7D32", "#C62828", values);
      } else {
        const base = getColor(heatKey);
        heatScale = makeHeatScale(base, values, inputs.heatDomain);
      }
    }


  else if(inputs.ActionMode==="filter"){

  mode="heat"
  heatKey = "ActionTargetKey"


  const concentration = inputs.ActionTargetConcentration || "all";
  const keepSet = getConcentrationSet(byId, geoData?.features || [], group, inputs.ActionTargetSubVariables?.TargetSubVariable1, concentration, inputs.geoLevel);

  const toPct = (x) => !Number.isFinite(x) ? null : x;
  const valuesForScale = (filteredMetricRows || [])
    .map((row) => {
      let val1;
      if (inputs.ActionTargetSubVariables.TargetSubVariable1 === "net") {
        const pos = sumKeys(group.TargetVariable1.polarity.pos, row);
        const neg = sumKeys(group.TargetVariable1.polarity.neg, row);
        val1 = (Number.isFinite(pos) && Number.isFinite(neg)) ? (pos - neg) : null;
      } else {
        val1 = Number(row[inputs.ActionTargetSubVariables.TargetSubVariable1]);
      }
      const p1 = toPct(val1);
      return Number.isFinite(p1) ? p1 : NaN;
    });

  const values = geoData.features.map(f => {
    const rid = normalizeAreaId(f.properties?.area_id ?? "");
    const row = byId.get(rid);



       // inside restyle(), filter branch where you currently compute val1/val2 and ActionTargetKey
if (row) {
  // --- compute val1 ---
  let val1;
  if (inputs.ActionTargetSubVariables.TargetSubVariable1 === "net") {
    const pos = sumKeys(group.TargetVariable1.polarity.pos, row);
    const neg = sumKeys(group.TargetVariable1.polarity.neg, row);
    val1 = (Number.isFinite(pos) && Number.isFinite(neg)) ? (pos - neg) : null;
    row.net1 = val1; // keep for tooltip
  } else {
    val1 = Number(row[inputs.ActionTargetSubVariables.TargetSubVariable1]);
  }

  // --- normalize to percent (handle 0..1 vs 0..100 robustly) ---
  const p1 = toPct(val1);

  // --- pass/fail by concentration ---
  const pass1 = Number.isFinite(p1) && (keepSet == null || keepSet.has(normalizeAreaId(row.area_id ?? "")));

  row.ActionTargetKey = pass1 ? p1 : null; // single-variable filter
}


      return row ? Number(row.ActionTargetKey) : NaN;
    });


  if (inputs.ActionTargetSubVariables?.TargetSubVariable1 === "net") {
    heatScale = make2pointHeatScale("#2E7D32", "#C62828", valuesForScale);
  } else {
    const base = colors?.[inputs.ActionTargetSubVariables?.TargetSubVariable1] || "#2E7D32";
    heatScale = makeHeatScale(base, valuesForScale);
  }

  }
    const compareGroup = inputs.ActionMode === "filter" ? group?.TargetVariable1 : group;
    const compareLabelsMap = compareGroup?.labels || {};
    const comparePolarity = compareGroup?.polarity || {};
    const isVoteIntention = String(compareGroup?.label || "").toLowerCase().includes("vote intention");
    const isDontKnowOption = (k) => {
      const kk = String(k || "").toLowerCase();
      const lbl = String(compareLabelsMap?.[k] || "").toLowerCase();
      return kk.includes("dontknow") || lbl.includes("don't know") || lbl.includes("dont know");
    };
    const movementKeys = (compareGroup?.keys || []).filter((k) => !(isVoteIntention && isDontKnowOption(k)));
    const compareKey = inputs.compareKey || "__top__";
    const isTopPositiveMover = inputs.compareWaves && compareKey === "__top__";
    const isTopNegativeMover = inputs.compareWaves && compareKey === "__top_neg__";
    let compareTopById = null;
    let compareSelectedValueMap = null;
    let compareSelectedScale = null;
    let compareSelectedKey = movementKeys[0] || null;

    if (inputs.compareWaves) {
      if (isTopPositiveMover || isTopNegativeMover) {
        compareTopById = new Map();
        let maxMag = 0;
        filteredMetricRows.forEach((row) => {
          const id = normalizeAreaId(row?.area_id ?? "");
          if (!id) return;
          let best = null;
          movementKeys.forEach((k) => {
            const d = Number(row?.[k]);
            if (!Number.isFinite(d)) return;
            if (isTopNegativeMover) {
              if (d >= 0) return;
              if (!best || d < best.delta) best = { key: k, delta: d };
            } else {
              if (d <= 0) return;
              if (!best || d > best.delta) best = { key: k, delta: d };
            }
          });
          if (best) {
            compareTopById.set(id, best);
            maxMag = Math.max(maxMag, Math.abs(best.delta));
          }
        });
        compareTopById.maxMag = maxMag || 1;
      } else {
        compareSelectedKey =
          compareKey === "net" && comparePolarity ? "net" :
          (movementKeys.includes(compareKey) ? compareKey : (movementKeys[0] || compareKey));
        compareSelectedValueMap = new Map();
        const deltaVals = [];
        filteredMetricRows.forEach((row) => {
          const id = normalizeAreaId(row?.area_id ?? "");
          if (!id) return;
          let v = NaN;
          if (compareSelectedKey === "net" && comparePolarity) {
            const pos = sumKeys(comparePolarity.pos, row);
            const neg = sumKeys(comparePolarity.neg, row);
            v = Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : NaN;
          } else {
            v = Number(row?.[compareSelectedKey]);
          }
          if (!Number.isFinite(v)) return;
          compareSelectedValueMap.set(id, v);
          deltaVals.push(v);
        });
        compareSelectedScale = make2pointHeatScale("#15803d", "#b91c1c", deltaVals);
      }
    }
    let compareArrows = null;
    if (inputs.compareWaves && Array.isArray(geoData?.features) && byId.size) {
      const valueKey = compareSelectedKey === "net" ? "Net" : (compareSelectedKey || (heatKey === "net" ? "Net" : heatKey));
      const values = geoData.features
        .map((f) => {
          const id = normalizeAreaId(f.properties?.area_id ?? "");
          const row = byId.get(id);
          const val = compareSelectedKey === "net"
            ? Number(row?.Net ?? row?.net1 ?? row?.net ?? NaN)
            : Number(row?.[valueKey]);
          return Number.isFinite(val) ? val : NaN;
        })
        .filter((v) => Number.isFinite(v));
      const maxAbs = values.length ? Math.max(...values.map((v) => Math.abs(v))) : 0;
      const featureCount = geoData.features.length || 0;
      const densityScale =
        featureCount > 1200 ? 0.35 :
        featureCount > 800  ? 0.45 :
        featureCount > 500  ? 0.60 :
        featureCount > 300  ? 0.75 : 1;
      // Nonlinear scale: small changes get ~0.35, largest get 1.8
      // Using sqrt compression so mid-sized changes are clearly visible
      const sizeScale = (v) => {
        if (!Number.isFinite(v) || !maxAbs) return 0;
        const t = Math.sqrt(Math.min(1, Math.abs(v) / maxAbs)); // sqrt for contrast
        return (0.3 + t * 1.5) * densityScale; // scale down in dense views
      };
      // Opacity also scales with magnitude for extra depth
      const opacityScale = (v) => {
        if (!Number.isFinite(v) || !maxAbs) return 0;
        const t = Math.min(1, Math.abs(v) / maxAbs);
        return (0.45 + t * 0.5) * densityScale + (1 - densityScale) * 0.2;
      };

      compareArrows = (isTopPositiveMover || isTopNegativeMover) ? null : {
        type: "FeatureCollection",
        features: geoData.features
          .map((f) => {
            const id = normalizeAreaId(f.properties?.area_id ?? "");
            const row = byId.get(id);
            if (!row) return null;
            const v = compareSelectedKey === "net"
              ? Number(row?.Net ?? row?.net1 ?? row?.net ?? NaN)
              : Number(row?.[valueKey]);
            if (!Number.isFinite(v) || v === 0) return null;
            const centroid = getFeatureCentroid(f);
            if (!centroid) return null;
            // 45° (NE) for positive gain, 225° (SW) for negative — 
            // diagonal arrows read more naturally as a trend direction map.
            // Mapbox rotates clockwise from north.
            const angle = v >= 0 ? 45 : 225;
            return {
              type: "Feature",
              geometry: { type: "Point", coordinates: centroid },
              properties: {
                angle,
                size: sizeScale(v),
                opacity: opacityScale(v),
                color: v >= 0 ? "#15803d" : "#b91c1c",
                delta: v,
              },
            };
          })
          .filter(Boolean),
        };
    }
    if (inputs.compareWaves) {
      console.log("[compare] arrows", {
        level: inputs.geoLevel,
        rows: filteredMetricRows.length,
        arrows: compareArrows?.features?.length || 0,
        wavePair: inputs.compareWavePair,
      });
    }

    // Categorical "top" (winner-take-all) maps used to fill every district with a
    // flat category colour — a 26%-margin win and a 45%-margin win looked
    // identical. Shade by the winning share's real min–max across the map so
    // colour also carries strength, not just which option won. Floor at 32%
    // saturation so the weakest winner still reads as "has data", not blank.
    // Opt-in via inputs.topStrengthShading — this pipeline is shared by every
    // dashboard in the app, not just the NJI story, so the default (flat colour)
    // stays unchanged everywhere that doesn't explicitly ask for this.
    let topShareDomain = null;
    if (inputs.topStrengthShading && mode === "top" && Array.isArray(group?.keys) && group.keys.length) {
      let mn = Infinity, mx = -Infinity;
      for (const row of byId.values()) {
        let best = -Infinity;
        for (const k of group.keys) {
          const v = Number(row[k]);
          if (Number.isFinite(v) && v > best) best = v;
        }
        if (Number.isFinite(best)) {
          if (best < mn) mn = best;
          if (best > mx) mx = best;
        }
      }
      if (Number.isFinite(mn) && Number.isFinite(mx) && mx > mn) topShareDomain = [mn, mx];
    }

    const styled = {
      type: "FeatureCollection",
      features: geoData.features.map(f => {
        const rid = normalizeAreaId(f.properties?.area_id ?? "");
        const row = byId.get(rid);
        
  
        if (!row) {
          return {
            ...f,
            properties: {
              ...f.properties,
              fillColor: "#ffffff",
              topLabel: "No data",
              topValue: 0,
              _allValues: {},
              _allValuesSorted: [],
            }
          };
        }

          let groupKeys
          if(inputs.ActionMode==="explore"){
            groupKeys = [
                          ...( group?.keys || []),
                            "Net"

                        ];
          } else if(inputs.ActionMode==="filter"){
            groupKeys = [
                          ...(group?.TargetVariable1?.keys || []),
                            "ActionTargetKey",
                            "net1",
                            "Net"

                        ];
          }

        const displayKeys = (groupKeys || []).filter(k => k in row);
        if (displayKeys.length === 0) {
          return {
            ...f,
            properties: {
              ...f.properties,
              fillColor: "#ffffff",
              topLabel: "No data",
              topValue: 0,
              _allValues: {},
              _allValuesSorted: [],
            }
          };
        }
  
        const entries = displayKeys
          .map(k => ({
            key: k,
            label: labels[k] || k,
            value: Number(row[k]),
          }))
          .filter(e => Number.isFinite(e.value))
          .sort((a, b) => b.value - a.value);

        // For "Top option" views, exclude Net keys from ranking so colors align to options
        const topEntries = entries.filter(e => !["Net", "net", "net1"].includes(e.key));



        // here to allow the "top","second","third" options, change the value which is selected from the ordered list
        let orderNumber 
        switch(inputs.preferenceFilter){
          case "top":
            orderNumber=0
            break;
          case "second":
            orderNumber=1
            break;
          case "third":
            orderNumber=2
            break;
          default:
            orderNumber=0
        }
  
        let fillColor = "#666";
        const ranked = topEntries.length ? topEntries : entries;
        let topKey    = ranked[orderNumber]?.key;
        let topLabel  = ranked[orderNumber]?.label || "";
        let topValue  = ranked[orderNumber]?.value || 0;
        

        if (inputs.compareWaves && (compareTopById || compareSelectedValueMap)) {
          if (compareTopById) {
            const rec = compareTopById.get(rid);
            if (!rec) {
              fillColor = "#d1d5db";
              topKey = null;
              topLabel = "No meaningful movement";
              topValue = 0;
            } else {
              const t = 0.2 + 0.8 * Math.sqrt(Math.abs(rec.delta) / (compareTopById.maxMag || 1));
              const base = getColor(rec.key);
              fillColor = mixWithWhite(base, t);
              topKey = rec.key;
              topLabel = `${compareLabelsMap[rec.key] || rec.key} ${isTopNegativeMover ? "negative" : "positive"} movement`;
              topValue = rec.delta;
            }
          } else if (compareSelectedValueMap) {
            const v = compareSelectedValueMap.get(rid);
            fillColor = Number.isFinite(v) ? compareSelectedScale(v) : "#e5e7eb";
            topKey = compareSelectedKey;
            topLabel = `${compareLabelsMap[compareSelectedKey] || compareSelectedKey} movement`;
            topValue = Number.isFinite(v) ? v : 0;
          }
        } else if (mode === "heat") {
          let v;
          if(inputs.ActionMode==="explore"){
            // Must go through readMetric: composite keys ("net", "negsum",
            // "sum:a|b") are derived, not columns on the row.
            v = readMetric(row, heatKey);
          } else if(inputs.ActionMode==="filter"){
             // ... [Keep your existing filter logic here] ...
             // (Assuming target pass checks are done above)
             if(row.ActionTargetKey != null) v = Number(row.ActionTargetKey);
             else v = null; 
          }
          
          fillColor = heatScale(v);
          topKey    = heatKey;
          topLabel  = labels[heatKey] || heatKey;
          topValue  = Number.isFinite(v) ? v : 0;
        } else if (topShareDomain) {
          const [mn, mx] = topShareDomain;
          const t = Math.max(0, Math.min(1, (topValue - mn) / (mx - mn)));
          fillColor = mixWithWhite(getColor(topKey), 0.32 + t * 0.68);
        } else {
          fillColor = getColor(topKey); // FIX: Use dictionary color for "Top" view
        }

        let sortedvals
        switch(inputs.group?.type){
          case "likert": {
            const keyOrder = (group?.keys || []).reduce((m, k, i) => { m[k] = i; return m; }, {});
            sortedvals = [...entries].sort((a, b) => {
              if (a.key === "Net") return 1;
              if (b.key === "Net") return -1;
              const ao = keyOrder[a.key] ?? Number.MAX_SAFE_INTEGER;
              const bo = keyOrder[b.key] ?? Number.MAX_SAFE_INTEGER;
              return ao - bo;
            });
            break;
          }
          case "price_elast":{
            const desiredOrder = ["expect", "expensive", "tooexpensive"];
            // Map polarity values back to their keys (expect, expensive, tooexpensive)
            const labelToKey = Object.fromEntries(
                Object.entries(inputs.group?.polarity).map(([key, val]) => [val, key])
              );

              sortedvals = entries
                .map(e => ({
                  ...e,
                  order: desiredOrder.indexOf(labelToKey[e.key]) // use e.key here
                }))
                .sort((a, b) => a.order - b.order);
              break;
            }
          default:
           sortedvals = entries
                  .map(e => ({
                    ...e,
                    order: e.key === "Net" ? Number.MAX_SAFE_INTEGER : 0 // net at bottom, others stay in original order
                  }))
                  .sort((a, b) => a.order - b.order)
                  .map(e => ({ key: e.key, label: e.label, value: e.value })); // remove order after sorting


        }
     
        return {
          ...f,
          properties: {
            ...f.properties,
            fillColor,
            topKey,
            topLabel,
            topValue,
            total_sum: row?.total_sum ?? null,
            _allValues: Object.fromEntries(entries.map(e => [e.key,e.label, e.value])),
            _allValuesSorted: sortedvals,
          }
        };
      })
    };

  
// ---- Build ranking list for the side panel (simple & consistent) ----
// ---- Build ranked list for TopAreasPanel (0..1 score) ----
let ranked = [];
try {
  const currentLevel = inputs.geoLevel;
  const normRankId = (id) => normalizeAreaIdForLevel(currentLevel, id);
  const featureList = inputs.geoData?.features || [];
  const isZipLevel = inputs.geoLevel === "zipcodes";
  const rankedByCanonicalId = new Map();

  (filteredMetricRows || []).forEach((r) => {
    const rawId = String(r.area_id ?? "");
    const normalizedId = normRankId(rawId);
    if (!normalizedId) return;

    let score;
    if (inputs.compareWaves && inputs.ActionMode === "filter") {
      const subVar = inputs.ActionTargetSubVariables?.TargetSubVariable1;
      if (subVar === "net") {
        const g = group?.TargetVariable1 || group;
        const pos = g?.polarity?.pos ? sumKeys(g.polarity.pos, r) : null;
        const neg = g?.polarity?.neg ? sumKeys(g.polarity.neg, r) : null;
        const v = (pos != null && neg != null) ? (pos - neg) : null;
        score = Number.isFinite(v) ? v / 100 : null;
      } else {
        const v = Number(r?.[subVar]);
        score = Number.isFinite(v) ? v / 100 : null;
      }
    } else if (inputs.compareWaves) {
      if (compareTopById) {
        const rec = compareTopById.get(normalizedId);
        score = rec?.delta ?? null;
      } else if (compareSelectedValueMap) {
        const v = compareSelectedValueMap.get(normalizedId);
        score = Number.isFinite(v) ? v / 100 : null;
      } else {
        score = null;
      }
    } else if (inputs.ActionMode === "filter") {
      const v = Number(r.ActionTargetKey);
      score = Number.isFinite(v) ? v / 100 : null;
    } else {
      if (inputs.preferenceFilter === "net") {
        const g = inputs.group;
        const pos = g?.polarity?.pos ? sumKeys(g.polarity.pos, r) : null;
        const neg = g?.polarity?.neg ? sumKeys(g.polarity.neg, r) : null;
        const v = (pos != null && neg != null) ? (pos - neg) : null;
        score = v != null ? v / 100 : null;
      } else {
        const heatKey = (inputs.preferenceFilter) || (inputs.group?.keys?.[0]);
        const v = Number(r[heatKey]);
        score = Number.isFinite(v) ? v / 100 : null;
      }
    }

    if (score == null) return;

    const matchedFeature = findFeatureForAreaId(currentLevel, rawId, featureList)
      || findFeatureForAreaId(currentLevel, normalizedId, featureList);
    if (!matchedFeature) return;

    const canonicalId = normRankId(matchedFeature.properties?.area_id ?? normalizedId);
    const baseName = getFeatureDisplayName(currentLevel, matchedFeature.properties || {}) || canonicalId;
    const name = isZipLevel
      ? (String(baseName).includes(String(canonicalId)) ? baseName : `${canonicalId} - ${baseName}`)
      : baseName;
    const nextItem = { id: canonicalId, name, score };
    const prevItem = rankedByCanonicalId.get(canonicalId);
    if (!prevItem) {
      rankedByCanonicalId.set(canonicalId, nextItem);
      return;
    }
    const replace = inputs.compareWaves
      ? Math.abs(score) > Math.abs(prevItem.score)
      : score > prevItem.score;
    if (replace) rankedByCanonicalId.set(canonicalId, nextItem);
  });

  ranked = Array.from(rankedByCanonicalId.values())
    .sort((a, b) => {
      if (inputs.compareWaves) {
        const isLoserMode = inputs.compareKey === "__top_neg__";
        if (isLoserMode) return a.score - b.score;
        return Math.abs(b.score) - Math.abs(a.score);
      }
      return b.score - a.score;
    });
} catch { /* noop */ }

// Main output — return all channels for wrapper to dispatch.
return { styledGeo: styled, ranked, compareArrows,
         rankedToNotify: ranked || [],
         maxRangesToSet, waveOptionsToSet, waveSelectToSet };
}
